import React, { useState } from 'react';
import EnhancedSidebar from '../../components/ui/EnhancedSidebar';
import Header from '../../components/ui/Header';
import EnhancedBreadcrumb from '../../components/ui/EnhancedBreadcrumb';
import Input from '../../components/ui/Input';
import DynamicDropdown from '../../components/ui/DynamicDropdown';
import dynamicOptionsService from '../../utils/dynamicOptionsService';
import Button from '../../components/ui/Button';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useSidebar } from '../../contexts/SidebarContext';
import campaignService from '../../utils/campaignService';
import reviewAutomationService from '../../utils/reviewAutomationService';

const InstantRequestPage = () => {
  const { currentOrg } = useOrganization();
  const { sidebarExpanded, handleSidebarToggle } = useSidebar();
  const [form, setForm] = useState({ name: '', email: '', phone: '', service: null });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [primaryServices, setPrimaryServices] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [campaign, setCampaign] = useState(null);

  // Helper function to replace template variables
  const replaceTemplateVariables = (text) => {
    if (!text) return text;
    return text
      .replaceAll('{{contact.name}}', form.name || 'there')
      .replaceAll('{{business.name}}', currentOrg?.name || currentOrg?.business_name || 'our business')
      .replaceAll('{{service.name}}', form.service?.name || (campaign?.default_service || 'Service'));
  };

  React.useEffect(() => {
    const load = async () => {
      if (!currentOrg?.id) return;
      setLoadingOptions(true);
      try {
        const services = await dynamicOptionsService.getPrimaryServices(currentOrg.id);
        setPrimaryServices(services);
        const c = await campaignService.getCampaign(currentOrg.id);
        setCampaign(c || campaignService.getDefaultCampaign());
      } finally {
        setLoadingOptions(false);
      }
    };
    load();
  }, [currentOrg?.id]);

  const handleAddPrimaryService = async (name) => {
    const newService = await dynamicOptionsService.createPrimaryService(name, currentOrg?.id);
    setPrimaryServices(prev => [...prev, newService]);
    return newService;
  };

  const onChange = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const canSend = form.name && form.email && form.phone && !sending;

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    setResult(null);
    const res = await reviewAutomationService.createInstantRequest({
      name: form.name,
      email: form.email,
      phone: form.phone,
      serviceName: form.service?.name || null,
      serviceId: form.service?.id || null,
      organizationId: currentOrg?.id
    });
    setResult(res);
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header onSidebarToggle={handleSidebarToggle} sidebarExpanded={sidebarExpanded} />
      <EnhancedSidebar expanded={sidebarExpanded} onToggle={handleSidebarToggle} />
      <main className={`pt-16 transition-all duration-300 ease-in-out ${sidebarExpanded ? 'lg:pl-64' : 'lg:pl-16'}`}>
        <EnhancedBreadcrumb />
          <div className="max-w-none mx-auto p-3 sm:p-4 md:p-6 lg:max-w-[94%]">
            <div className="mb-6 sm:mb-8">
              <h1 className="text-2xl sm:text-3xl font-bold text-text-primary mb-2">Instant Request</h1>
              <p className="text-sm sm:text-base text-text-secondary">Send a one-off review request via email. The content uses your active Campaign settings.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-card rounded-lg border border-border p-6">
                <div className="space-y-4">
                  <Input label="Full Name" value={form.name} onChange={e => onChange('name', e.target.value)} placeholder="Full Name" />
                  <Input label="Email Address" value={form.email} onChange={e => onChange('email', e.target.value)} placeholder="name@company.com" />
                  <Input label="Phone Number" value={form.phone} onChange={e => onChange('phone', e.target.value)} placeholder="(555) 123-4567" />
                  <DynamicDropdown
                    label="Primary Service"
                    value={form.service}
                    onChange={(val) => onChange('service', val)}
                    options={primaryServices}
                    onAddOption={handleAddPrimaryService}
                    placeholder="Select or add primary service..."
                    addNewText="Add new service"
                    disabled={loadingOptions}
                  />
                  <Button variant="primary" disabled={!canSend} onClick={handleSend} loading={sending}>
                    Send Request
                  </Button>
                  {result && (
                    <div className={`text-sm ${result.success ? 'text-green-600' : 'text-red-600'}`}>
                      {result.success ? 'Request sent' : result.error}
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-card rounded-lg border border-border p-6">
                <h3 className="text-lg font-medium mb-4">Email Preview</h3>
                <div className="bg-surface rounded-md p-4 text-sm text-text-secondary border border-border">
                  <div className="mb-3">
                    <div className="text-sm font-medium text-text-primary">
                      {(campaign && campaign.email_subject)
                        ? replaceTemplateVariables(campaign.email_subject)
                        : `How was your experience with ${currentOrg?.name || currentOrg?.business_name || 'our business'}?`}
                    </div>
                    <div className="text-xs text-text-muted">{currentOrg?.name || currentOrg?.business_name || 'Our Business'} &lt;noreply@reviews.servicereview.ai&gt;</div>
                  </div>
                  <h4 className="text-lg font-semibold text-text-primary mb-2">{replaceTemplateVariables(campaign?.email_title) || 'How was our service?'}</h4>
                  <div className="whitespace-pre-line">
                    {(campaign && campaign.email_body_template)
                      ? replaceTemplateVariables(campaign.email_body_template)
                      : `Hi ${form.name.split(' ')[0] || 'there'},\n\nThank you for choosing ${currentOrg?.name || currentOrg?.business_name || 'our business'}${form.service?.name ? ` for ${form.service.name}` : ''}.\n\nYour feedback helps us improve. Could you take a moment to share your experience?`}
                  </div>
                  <div className="mt-4 inline-block bg-primary text-white px-4 py-2 rounded">{campaign?.cta_button_text || 'Leave us a review'}</div>
                </div>
                <p className="text-xs text-text-muted mt-3">This preview mirrors your current Campaign settings.</p>
              </div>
            </div>
          </div>
        </main>
    </div>
  );
};

export default InstantRequestPage;


